// The Racket House — FREE racket stringing builder (Dwell theme)  v2
//
// Renders the stringing picker on racket pages and, when the customer chooses
// "Yes", replaces the normal add-to-cart with one request that adds:
//   1. the racket (with the stringing job as line-item properties)
//   2. "Racket Stringing Labour — FREE" ($0), nested under the racket
//   3. the string charge(s), nested under the racket
//
// WHERE THE STRING COMES FROM — and what it does to inventory
// -----------------------------------------------------------
// Full set, in priority order:
//   1. open reel on the bench  -> String Charge ladder, nothing deducted
//   2. sealed sellable reel    -> String Charge ladder, nothing deducted
//   3. set on the wall         -> the real set variant, -1 inventory
// Hybrid (half a set per side):
//   - bench reel or sellable reel only, half price off the ladder, nothing deducted
//   - never a set: half a set can't be sold twice
// Natural gut is the exception — no reels exist for it:
//   - full set -> the 12m set variant, -1 inventory
//   - hybrid   -> the linked 6m product first (-1 on the 6m), 12m only if no 6m

const money = (cents) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format((Number(cents) || 0) / 100);

const TYPE_ORDER = ['Synthetic Gut', 'Multifilament', 'Natural Gut', 'Polyester / Monofilament', 'Hybrid Set'];
const TYPE_LABEL = { 'Hybrid Set': 'Hybrid Sets' };
const kg = (lbs) => (lbs * 0.45359237).toFixed(1);
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const clean = (s) => (s && s !== 'Default Title' ? String(s).trim() : '');

class TrhStringBuilder extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    try {
      this.strings = JSON.parse(this.querySelector('[data-trh-sb-strings]')?.textContent || '[]');
    } catch (e) {
      this.strings = [];
    }
    try {
      this.ladder = JSON.parse(this.querySelector('[data-trh-sb-ladder]')?.textContent || '[]')
        .map((r) => ({ id: Number(r.id), p: Number(r.p) }))
        .filter((r) => r.id && Number.isFinite(r.p))
        .sort((a, b) => a.p - b.p);
    } catch (e) {
      this.ladder = [];
    }
    // Hide the whole block until at least one string is available for this sport
    if (!this.strings.length) {
      this.hidden = true;
      return;
    }

    this.sport = this.dataset.sport;
    this.tMin = Number(this.dataset.tMin);
    this.tMax = Number(this.dataset.tMax);
    this.hardMin = Number(this.dataset.tHardMin);
    this.hardMax = Number(this.dataset.tHardMax);
    this.tMid = Math.round((this.tMin + this.tMax) / 2);
    this.want = false;
    this.mode = 'regular';
    this.pick = false;
    this.pickId = Number(this.dataset.pickId) || 0;
    this.pickPrice = Number(this.dataset.pickPrice) || 0;

    this.panel = this.querySelector('[data-trh-panel]');
    this.errorEl = this.querySelector('[data-trh-error]');
    this.summaryEl = this.querySelector('[data-trh-summary]');
    this.notesEl = this.querySelector('[data-trh-notes]');

    this.slots = {};
    for (const el of this.querySelectorAll('[data-trh-slot]')) {
      const key = el.dataset.trhSlot;
      this.slots[key] = {
        key,
        el,
        filter: 'all',
        productId: '',
        gauge: '',
        colour: '',
        variantId: '',
        tension: this.tMid,
        chips: el.querySelector('[data-trh-chips]'),
        select: el.querySelector('[data-trh-string]'),
        variant: el.querySelector('[data-trh-variant]'),
        colourSel: el.querySelector('[data-trh-colour]'),
        note: el.querySelector('[data-trh-string-note]'),
        tensionEl: el.querySelector('[data-trh-tension]'),
      };
    }

    this.querySelectorAll('[data-trh-want]').forEach((r) =>
      r.addEventListener('change', () => this.setWant(r.value === 'yes' && r.checked))
    );
    this.querySelectorAll('[data-trh-mode]').forEach((r) =>
      r.addEventListener('change', () => r.checked && this.setMode(r.value))
    );

    this.pickEl = this.querySelector('[data-trh-pick]');
    this.pickEl?.addEventListener('change', () => this.setPick(this.pickEl.checked));
    this.notesPlaceholder = this.notesEl?.getAttribute('placeholder') || '';

    for (const slot of Object.values(this.slots)) this.initSlot(slot);
    this.renderRange();
    this.renderSummary();

    // Listen on window (capture) so we run before the theme's delegated submit handler on document
    this._onSubmit = this.onSubmit.bind(this);
    window.addEventListener('submit', this._onSubmit, true);
  }

  disconnectedCallback() {
    if (this._onSubmit) window.removeEventListener('submit', this._onSubmit, true);
    this._ready = false;
  }

  /* ---------- state ---------- */

  get formComponent() {
    return document.getElementById(this.dataset.formId)?.closest('product-form-component') || null;
  }

  setWant(on) {
    this.want = on;
    this.panel.hidden = !on;
    this.classList.toggle('is-on', on);
    // Buy-now / Shop Pay buttons skip our add-to-cart, so hide them while stringing is chosen
    this.formComponent?.classList.toggle('trh-sb-stringing', on);
    this.clearError();
    this.renderSummary();
  }

  // "Too many choices — you pick for me": our stringer chooses, flat price
  setPick(on) {
    this.pick = !!on && !!this.pickId;
    this.classList.toggle('is-pick', this.pick);
    if (this.notesEl) {
      this.notesEl.setAttribute(
        'placeholder',
        this.pick ? this.dataset.pickNotes || 'Tell us about your game and we’ll pick the perfect string.' : this.notesPlaceholder
      );
    }
    for (const slot of Object.values(this.slots)) this.renderChips(slot);
    const t = this.slots.main?.el.querySelector('.trh-sb__tlabel');
    if (t) t.textContent = this.pick ? 'Tension (not sure? leave it — the middle is a great start)' : this.isHybrid ? 'Mains tension' : 'Tension';
    this.clearError();
    this.renderSummary();
  }

  get isHybrid() {
    return this.mode === 'hybrid' && !this.pick;
  }

  setMode(mode) {
    this.mode = mode;
    this.querySelectorAll('[data-trh-mode-help]').forEach((p) => (p.hidden = p.dataset.trhModeHelp !== mode));
    const cross = this.slots.cross;
    if (cross) cross.el.hidden = mode !== 'hybrid';
    const main = this.slots.main;
    main.el.querySelector('[data-trh-slot-title]').textContent = mode === 'hybrid' ? 'Mains:' : 'String:';
    // Pre-packaged hybrid sets only make sense in Regular mode; and a string that can't
    // be split (no reel, not natural gut) has to be dropped when switching to hybrid.
    for (const slot of Object.values(this.slots)) {
      if (mode === 'hybrid' && slot.filter === 'Hybrid Set') slot.filter = 'all';
      const prod = this.product(slot.productId);
      if (prod && !this.candidates(slot).some((s) => String(s.id) === String(prod.id))) {
        slot.productId = '';
        slot.gauge = '';
        slot.colour = '';
        slot.variantId = '';
      }
      this.renderChips(slot);
      this.renderSelect(slot);
      this.renderTension(slot);
    }
    this.clearError();
    this.renderSummary();
  }

  product(id) {
    return this.strings.find((s) => String(s.id) === String(id)) || null;
  }

  /* ---------- the charge ladder ---------- */

  // Half a set, rounded UP to the nearest 25c so odd prices never round in the customer's favour
  half(cents) {
    return Math.ceil(Number(cents) / 2 / 25) * 25;
  }

  // The ladder rung for an amount: exact if we have it, otherwise the next rung ABOVE.
  rung(cents) {
    const want = Math.round(Number(cents) || 0);
    for (const r of this.ladder) if (r.p >= want) return r;
    return this.ladder.length ? this.ladder[this.ladder.length - 1] : null;
  }

  /* ---------- availability ---------- */

  // Can we string a FULL set of this variant, from anywhere?
  usable(prod, v) {
    return v.q > 0 || v.b || v.r;
  }

  // Can we string HALF a set of this variant for a hybrid?
  // Normally: only off a reel (bench or sealed) — a set can't be split and resold.
  // Natural gut: no reels exist, so it comes off a 6m set, or a 12m set as a last resort.
  hybridUsable(prod, v) {
    if (prod.ng) return (prod.hs || []).some((h) => h.q > 0) || v.q > 0;
    return !!(v.b || v.r);
  }

  pickable(prod, v) {
    return this.mode === 'hybrid' ? this.hybridUsable(prod, v) : this.usable(prod, v);
  }

  candidates(slot) {
    const hybrid = this.mode === 'hybrid';
    return this.strings.filter((s) => {
      if (hybrid && s.ty === 'Hybrid Set') return false;
      if (!s.v.some((v) => this.pickable(s, v))) return false;
      if (slot.filter !== 'all' && s.ty !== slot.filter) return false;
      return true;
    });
  }

  /* ---------- rendering ---------- */

  initSlot(slot) {
    slot.select.addEventListener('change', () => {
      slot.productId = slot.select.value;
      slot.gauge = '';
      slot.colour = '';
      slot.variantId = '';
      this.renderVariant(slot);
      this.clearError();
      this.renderSummary();
    });
    slot.variant.addEventListener('change', () => {
      if (slot.variant.dataset.mode === 'direct') {
        slot.variantId = slot.variant.value;
        slot.gauge = '';
        slot.colour = '';
        if (slot.colourSel) slot.colourSel.hidden = true;
      } else {
        slot.gauge = slot.variant.value;
        slot.colour = '';
        slot.variantId = '';
        this.renderColour(slot);
      }
      this.renderNote(slot);
      this.clearError();
      this.renderSummary();
    });
    slot.colourSel?.addEventListener('change', () => {
      slot.colour = slot.colourSel.value;
      slot.variantId = this.resolve(slot);
      this.renderNote(slot);
      this.clearError();
      this.renderSummary();
    });
    this.renderChips(slot);
    this.renderSelect(slot);
    this.renderTension(slot);
  }

  renderChips(slot) {
    const present = new Set(
      this.strings
        .filter((s) => s.ty && !(this.mode === 'hybrid' && s.ty === 'Hybrid Set') && s.v.some((v) => this.pickable(s, v)))
        .map((s) => s.ty)
    );
    const types = TYPE_ORDER.filter((t) => present.has(t));
    const canPick = !!this.pickId && slot.key === 'main';
    slot.chips.innerHTML = '';
    if (types.length < 2 && !canPick) {
      slot.chips.hidden = true;
      return;
    }
    slot.chips.hidden = false;
    const mk = (value, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'trh-sb__chip' + (slot.filter === value ? ' is-active' : '');
      b.textContent = label;
      b.setAttribute('aria-pressed', String(slot.filter === value));
      b.addEventListener('click', () => {
        slot.filter = value;
        // picking a type means they want to choose it themselves
        if (this.pick) {
          this.setPick(false);
        } else {
          this.renderChips(slot);
          this.renderSelect(slot);
        }
      });
      slot.chips.appendChild(b);
    };
    if (types.length >= 2) {
      mk('all', this.sport === 'tennis' ? 'All Strings' : 'All');
      types.forEach((t) => mk(t, TYPE_LABEL[t] || t));
    }
    if (canPick) slot.chips.appendChild(this.pickChip());
  }

  // "Surprise me" sits at the end of the string-type chips
  pickChip() {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'trh-sb__chip trh-sb__chip--pick' + (this.pick ? ' is-active' : '');
    b.setAttribute('aria-pressed', String(this.pick));
    const emoji = this.dataset.pickEmoji || '';
    b.innerHTML =
      (emoji ? `<span class="trh-sb__chip-emoji" aria-hidden="true">${emoji}</span>` : '') +
      `${this.dataset.pickChip || 'Surprise me'}<span class="trh-sb__chip-price">${money(this.pickPrice)}</span>`;
    b.addEventListener('click', () => this.setPick(!this.pick));
    return b;
  }

  // What the string costs, before a gauge is chosen. Prices can differ per gauge,
  // so show the cheapest pickable one and say "from" when they aren't all the same.
  priceFor(prod) {
    const vs = prod.v.filter((v) => this.pickable(prod, v));
    if (!vs.length) return '';
    const hybrid = this.mode === 'hybrid';
    const amounts = vs.map((v) => this.lineFor(prod, v, hybrid).price).filter((n) => Number.isFinite(n));
    if (!amounts.length) return '';
    const lo = Math.min(...amounts);
    const hi = Math.max(...amounts);
    const label = money(lo) + (hi !== lo ? '+' : '');
    return hybrid ? `½ set ${label}` : label;
  }

  renderSelect(slot) {
    const list = this.candidates(slot);
    const sel = slot.select;
    sel.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = list.length ? 'Select…' : 'No strings in this category right now';
    sel.appendChild(ph);
    for (const s of list) {
      const o = document.createElement('option');
      o.value = String(s.id);
      o.textContent = `${s.t} — ${this.priceFor(s)}`;
      sel.appendChild(o);
    }
    if (slot.productId && list.some((s) => String(s.id) === String(slot.productId))) {
      sel.value = String(slot.productId);
    } else if (slot.productId && slot.filter !== 'all') {
      // keep the chosen string even if it's outside the current chip filter
      const prod = this.product(slot.productId);
      if (prod) {
        const o = document.createElement('option');
        o.value = String(prod.id);
        o.textContent = `${prod.t} — ${this.priceFor(prod)}`;
        sel.appendChild(o);
        sel.value = String(prod.id);
      }
    } else {
      slot.productId = '';
      slot.gauge = '';
      slot.colour = '';
      slot.variantId = '';
    }
    this.renderVariant(slot);
  }

  // Step 2: gauge. Every set and reel comes AS a gauge, so this always narrows first.
  renderVariant(slot) {
    const prod = this.product(slot.productId);
    const gsel = slot.variant;
    const csel = slot.colourSel;
    gsel.innerHTML = '';
    delete gsel.dataset.mode;
    if (csel) {
      csel.innerHTML = '';
      csel.hidden = true;
    }
    if (!prod) {
      gsel.hidden = true;
      slot.gauge = '';
      slot.colour = '';
      slot.variantId = '';
      this.renderNote(slot);
      return;
    }

    const vs = prod.v.filter((v) => this.pickable(prod, v));
    const gauges = [...new Set(vs.map((v) => clean(v.g)).filter(Boolean))];

    // No gauge option on this product — fall back to one plain list of whatever it does have.
    if (!gauges.length) {
      gsel.dataset.mode = 'direct';
      const labels = vs.map((v) => clean(v.t));
      if (vs.length === 1 && !labels[0]) {
        slot.variantId = String(vs[0].id);
        gsel.hidden = true;
      } else {
        gsel.hidden = false;
        const ph = document.createElement('option');
        ph.value = '';
        ph.textContent = 'Gauge / colour…';
        gsel.appendChild(ph);
        for (const v of vs) {
          const o = document.createElement('option');
          o.value = String(v.id);
          o.textContent = clean(v.t) || 'Standard';
          gsel.appendChild(o);
        }
        if (vs.length === 1) slot.variantId = String(vs[0].id);
        gsel.value = slot.variantId && vs.some((v) => String(v.id) === slot.variantId) ? slot.variantId : '';
        slot.variantId = gsel.value;
      }
      this.renderNote(slot);
      return;
    }

    gsel.hidden = false;
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = 'Gauge…';
    gsel.appendChild(ph);
    for (const g of gauges) {
      const o = document.createElement('option');
      o.value = g;
      o.textContent = g;
      gsel.appendChild(o);
    }
    // Only one gauge in stock? Choose it for them, but leave it on screen so they can see it.
    if (gauges.length === 1) slot.gauge = gauges[0];
    gsel.value = gauges.includes(slot.gauge) ? slot.gauge : '';
    slot.gauge = gsel.value;
    this.renderColour(slot);
  }

  // Step 3: colour — only when this gauge actually comes in more than one.
  renderColour(slot) {
    const prod = this.product(slot.productId);
    const csel = slot.colourSel;
    if (!csel) {
      slot.variantId = this.resolve(slot);
      return;
    }
    csel.innerHTML = '';
    if (!prod || !slot.gauge) {
      csel.hidden = true;
      slot.colour = '';
      slot.variantId = '';
      this.renderNote(slot);
      return;
    }
    const vs = prod.v.filter((v) => this.pickable(prod, v) && clean(v.g) === slot.gauge);
    const colours = [...new Set(vs.map((v) => clean(v.c)).filter(Boolean))];

    if (colours.length < 2) {
      // One colour (or none recorded) — no choice to make, don't ask for one.
      csel.hidden = true;
      slot.colour = colours[0] || '';
      slot.variantId = vs.length ? String(vs[0].id) : '';
      this.renderNote(slot);
      return;
    }
    csel.hidden = false;
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = 'Colour…';
    csel.appendChild(ph);
    for (const c of colours) {
      const o = document.createElement('option');
      o.value = c;
      o.textContent = c;
      csel.appendChild(o);
    }
    csel.value = colours.includes(slot.colour) ? slot.colour : '';
    slot.colour = csel.value;
    slot.variantId = this.resolve(slot);
    this.renderNote(slot);
  }

  resolve(slot) {
    const prod = this.product(slot.productId);
    if (!prod || !slot.gauge) return '';
    const v = prod.v.find(
      (x) =>
        this.pickable(prod, x) &&
        clean(x.g) === slot.gauge &&
        (!slot.colour || clean(x.c) === slot.colour)
    );
    return v ? String(v.id) : '';
  }

  renderNote(slot) {
    const prod = this.product(slot.productId);
    const v = prod ? prod.v.find((x) => String(x.id) === String(slot.variantId)) : null;
    let msg = '';
    if (prod && prod.ty === 'Hybrid Set' && this.mode === 'regular') {
      msg = 'Pre-packaged hybrid — mains and crosses are both included in this set.';
    } else if (prod && v && this.mode === 'hybrid' && prod.ng) {
      const sixm = (prod.hs || []).some((h) => h.q > 0);
      msg = sixm
        ? 'Natural gut — cut from a 6m half set, so nothing goes to waste.'
        : 'No 6m half sets left, so this comes off a full 12m set and is charged as one.';
    }
    slot.note.textContent = msg;
    slot.note.hidden = !msg;
  }

  renderTension(slot) {
    const label =
      this.mode === 'hybrid' ? (slot.key === 'cross' ? 'Crosses tension' : 'Mains tension') : 'Tension';
    slot.tensionEl.innerHTML = `
      <span class="trh-sb__tlabel">${label}</span>
      <div class="trh-sb__stepper">
        <button type="button" class="trh-sb__step" data-d="-1" aria-label="Decrease tension">−</button>
        <label class="trh-sb__tval">
          <input type="number" inputmode="numeric" min="${this.hardMin}" max="${this.hardMax}" step="1" value="${slot.tension}" aria-label="${label} in pounds">
          <span>lbs <small data-kg>(${kg(slot.tension)} kg)</small></span>
        </label>
        <button type="button" class="trh-sb__step" data-d="1" aria-label="Increase tension">+</button>
      </div>
      <p class="trh-sb__twarn" hidden></p>`;
    const input = slot.tensionEl.querySelector('input');
    const update = (val) => {
      let n = Math.round(Number(val));
      if (!Number.isFinite(n)) n = this.tMid;
      n = Math.min(this.hardMax, Math.max(this.hardMin, n));
      slot.tension = n;
      input.value = n;
      slot.tensionEl.querySelector('[data-kg]').textContent = `(${kg(n)} kg)`;
      const warn = slot.tensionEl.querySelector('.trh-sb__twarn');
      const out = n < this.tMin || n > this.tMax;
      warn.hidden = !out;
      warn.textContent = out ? `Outside the ${this.tMin}–${this.tMax} lbs range — we'll string it as requested.` : '';
      this.renderSummary();
    };
    slot.tensionEl.querySelectorAll('[data-d]').forEach((b) =>
      b.addEventListener('click', () => update(slot.tension + Number(b.dataset.d)))
    );
    input.addEventListener('change', () => update(input.value));
    update(slot.tension);
  }

  renderRange() {
    const el = this.querySelector('[data-trh-range]');
    if (!el) return;
    const lead = this.dataset.tSource === 'manufacturer' ? 'Manufacturer specified range' : `Recommended ${this.sport} range`;
    el.textContent = `${lead}: ${this.tMin} to ${this.tMax} lbs. (mid: ${this.tMid} lbs)`;
  }

  /* ---------- pricing / cart lines ---------- */

  qty(form) {
    const fd = new FormData(form);
    return Math.max(1, parseInt(fd.get('quantity'), 10) || 1);
  }

  label(prod, v) {
    const bits = [clean(v.g), clean(v.c)].filter(Boolean);
    const vt = bits.length ? bits.join(' · ') : clean(v.t);
    return { vt, name: vt ? `${prod.t} (${vt})` : prod.t };
  }

  // Decide the cart line for one variant. `qty` is how many rackets.
  // Returns {id, price, source, deducts} or {error}.
  lineFor(prod, v, hybrid, qty = 1) {
    if (!hybrid) {
      // 1. bench reel  2. sellable reel  3. set on the wall
      if (v.b) return this.ladderLine(v.p, 'full set from the reel on our bench');
      if (v.r) return this.ladderLine(v.p, 'full set from a reel');
      if (v.q >= qty) return { id: v.id, price: v.p, source: 'Full set', deducts: true };
      return { error: 'stock' };
    }

    // Hybrid — natural gut is the only string that may come off a set.
    if (prod.ng) {
      const six = this.sixFor(prod, v, qty);
      if (six) return { id: six.id, price: six.p, source: '6m half set', deducts: true };
      if (v.q >= qty) return { id: v.id, price: v.p, source: 'full 12m set (no 6m in stock)', deducts: true };
      return { error: 'stock' };
    }
    if (v.b) return this.ladderLine(this.half(v.p), 'half set from the reel on our bench');
    if (v.r) return this.ladderLine(this.half(v.p), 'half set from a reel');
    return { error: 'hybrid' };
  }

  // The 6m half set to draw from, matching gauge where the 6m product records one.
  sixFor(prod, v, qty = 1) {
    const hs = (prod.hs || []).filter((h) => h.q >= qty);
    if (!hs.length) return null;
    const g = clean(v.g);
    const c = clean(v.c);
    return (
      hs.find((h) => clean(h.g) === g && clean(h.c) === c) ||
      hs.find((h) => clean(h.g) === g) ||
      hs[0]
    );
  }

  ladderLine(cents, source) {
    const r = this.rung(cents);
    if (!r) return { error: 'ladder' };
    return { id: r.id, price: r.p, source, deducts: false };
  }

  // Returns {id, price, name, vt, use} for a slot, or {error}
  charge(slot, role, qty) {
    if (this.pick) {
      return {
        id: this.pickId,
        price: this.pickPrice,
        name: this.dataset.pickName || "Surprise me — stringer's pick",
        use: "Surprise me — stringer picks the string",
      };
    }
    const prod = this.product(slot.productId);
    if (!prod) return { error: role === 'Crosses' ? 'Please choose a string for the crosses.' : 'Please choose a string.' };
    const v = prod.v.find((x) => String(x.id) === String(slot.variantId));
    if (!v) {
      if (slot.gauge && slot.colourSel && !slot.colourSel.hidden && !slot.colour) {
        return { error: `Please choose a colour for ${prod.t}.` };
      }
      return { error: `Please choose a gauge for ${prod.t}.` };
    }
    const { vt, name } = this.label(prod, v);
    const line = this.lineFor(prod, v, this.mode === 'hybrid', qty);

    if (line.error === 'hybrid') {
      return {
        error: `${name} isn't available as a half set right now — hybrids are strung from an open reel. Pick another string for this side, or switch to Regular.`,
      };
    }
    if (line.error === 'ladder') {
      return { error: `We couldn't price ${name}. Please contact us and we'll sort it out.` };
    }
    if (line.error) {
      return { error: `Only ${Math.max(0, v.q)} of ${name} in stock — choose another string or lower the quantity.` };
    }

    const use = this.mode === 'hybrid' ? `${role} · ${line.source}` : line.source;
    // A ladder line carries the hidden "String Charge" product, so the cart has to be
    // told what the customer actually bought. A real set variant already says it itself.
    return { id: line.id, price: line.price, name, vt, use, base: prod.t, ladder: !line.deducts };
  }

  renderSummary() {
    if (!this.summaryEl) return;
    if (!this.want) {
      this.summaryEl.innerHTML = '';
      return;
    }
    const form = document.getElementById(this.dataset.formId);
    const qty = form ? this.qty(form) : 1;
    const parts = [];
    let total = 0;
    const main = this.charge(this.slots.main, 'Mains', qty);
    if (!main.error) {
      parts.push(`<li><span>${this.isHybrid ? 'Mains' : 'String'}: ${main.name}</span><b>${money(main.price)}</b></li>`);
      total += main.price;
    }
    if (this.isHybrid && this.slots.cross) {
      const cross = this.charge(this.slots.cross, 'Crosses', qty);
      if (!cross.error) {
        parts.push(`<li><span>Crosses: ${cross.name}</span><b>${money(cross.price)}</b></li>`);
        total += cross.price;
      }
    }
    this.summaryEl.innerHTML = `
      <ul class="trh-sb__lines">
        <li><span>Stringing labour</span><b class="trh-sb__free">FREE</b></li>
        ${parts.join('')}
      </ul>
      <p class="trh-sb__total"><span>Stringing total${qty > 1 ? ` (× ${qty} rackets)` : ''}</span><b>${money(total * qty)}</b></p>`;
  }

  /* ---------- add to cart ---------- */

  onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.dataset.formId) return;
    if (!this.want || this.hidden) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addToCart(form);
  }

  showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async addToCart(form) {
    if (this._busy) return;
    const qty = this.qty(form);
    const hybrid = this.isHybrid;
    const main = this.charge(this.slots.main, 'Mains', qty);
    if (main.error) return this.showError(main.error);
    let cross = null;
    if (hybrid) {
      cross = this.charge(this.slots.cross, 'Crosses', qty);
      if (cross.error) return this.showError(cross.error);
    }
    this.clearError();

    const fd = new FormData(form);
    const racketId = Number(fd.get('id'));
    if (!racketId) return this.showError('Please choose your racket options first.');

    const tMain = this.slots.main.tension;
    const tCross = hybrid ? this.slots.cross.tension : tMain;
    const notes = (this.notesEl?.value || '').trim();

    const props = {};
    for (const [k, v] of fd.entries()) {
      const m = k.match(/^properties\[(.+)\]$/);
      if (m && v) props[m[1]] = v;
    }
    props['Stringing'] = this.pick ? 'Surprise me — FREE labour' : hybrid ? 'Hybrid — FREE labour' : 'Regular — FREE labour';
    if (this.pick) {
      props['String'] = "Surprise me! Stringer's pick";
      props['Tension'] = `${tMain} lbs (${kg(tMain)} kg)`;
    } else if (hybrid) {
      props['Mains'] = `${main.name} @ ${tMain} lbs`;
      props['Crosses'] = `${cross.name} @ ${tCross} lbs`;
    } else {
      props['String'] = main.name;
      props['Tension'] = `${tMain} lbs (${kg(tMain)} kg)`;
    }
    if (notes) props['Special instructions'] = notes;

    const racketTitle = this.dataset.productTitle || 'Racket';
    const items = [
      { id: racketId, quantity: qty, properties: props },
      {
        id: Number(this.dataset.labourId),
        quantity: qty,
        parent_id: racketId,
        properties: { Racket: racketTitle, Setup: this.pick ? `Surprise me ${tMain} lbs` : hybrid ? `Hybrid ${tMain}/${tCross} lbs` : `Regular ${tMain} lbs` },
      },
    ];
    const addString = (c) => {
      const p = { Racket: racketTitle, 'Used for': c.use };
      if (c.vt) p['Gauge / colour'] = c.vt;
      // A ladder charge carries the hidden ladder product, so the line has to name the
      // string itself. `_String` is underscored and feeds the cart's line title; the plain
      // `String` is what shows at checkout, on the order and on the stringer's copy, where
      // our theme doesn't run. The cart template hides the plain one to avoid saying it twice.
      if (c.ladder && c.base) {
        p['_String'] = c.base;
        p['String'] = c.base;
      }
      items.push({ id: Number(c.id), quantity: qty, parent_id: racketId, properties: p });
    };
    addString(main);
    if (cross) addString(cross);

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const body = { items };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    this._busy = true;
    this.classList.add('is-busy');
    const button = form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {
      events = null;
    }
    const Evt = events && events.CartLinesUpdateEvent;
    let deferred = null;
    const source = this.formComponent || this;
    if (Evt) {
      deferred = Evt.createPromise();
      source.dispatchEvent(
        new Evt({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    }

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (!res.ok || data.status) {
        this.showError(data.description || data.message || 'Sorry — we could not add that to your cart.');
        deferred?.resolve({
          cart: Evt.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'trh-string-builder', itemCount: qty, productId: this.dataset.productId },
        });
        return;
      }

      if (deferred) {
        deferred.resolve({
          cart: Evt.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'trh-string-builder',
            itemCount: qty,
            productId: this.dataset.productId,
            sections: data.sections,
            didError: false,
          },
        });
      } else {
        window.location.href = `${root()}cart`;
      }
    } catch (err) {
      console.error('[trh-string-builder]', err);
      deferred?.reject(err);
      this.showError('Sorry — something went wrong adding stringing to your cart. Please try again.');
    } finally {
      this._busy = false;
      this.classList.remove('is-busy');
      button?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('trh-string-builder')) customElements.define('trh-string-builder', TrhStringBuilder);

// Keep the summary's "× N rackets" in sync with the quantity selector
document.addEventListener('change', (e) => {
  if (!(e.target instanceof HTMLInputElement) || e.target.name !== 'quantity') return;
  document.querySelectorAll('trh-string-builder').forEach((el) => el.renderSummary && el.want && el.renderSummary());
});
